import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import {alpha} from '@mui/system';
import {useBreadcrumb, useResource, useTranslate} from '@refinedev/core';
import {CreateProps, type SaveButtonProps} from '@refinedev/mui';
import {FC, ReactNode} from 'react';

import {SaveButton} from '@components/buttons/save';
import {CustomBreadcrumbs} from '@minimal/components/custom-breadcrumbs';

type CustomCreateProps = Omit<CreateProps, 'title'> & {
  title?: string | ReactNode;
};

export const CustomCreate: FC<CustomCreateProps> = ({
  title,
  children,
  saveButtonProps: saveButtonPropsFromProps,
  resource: resourceFromProps,
  isLoading = false,
  headerButtonProps,
  headerButtons,
  footerButtonProps,
  footerButtons,
}) => {
  const translate = useTranslate();

  const {resource} = useResource(resourceFromProps);

  const {breadcrumbs} = useBreadcrumb();

  const saveButtonProps: SaveButtonProps = {
    ...(isLoading ? {disabled: true} : {}),
    ...saveButtonPropsFromProps,
  };

  const defaultFooterButtons = <SaveButton {...saveButtonProps} />;

  return (
    <>
      <CustomBreadcrumbs
        heading={title?.toString() ?? translate(`${resource?.meta?.prefix}.titles.create`)}
        links={breadcrumbs.map((item) => ({
          ...item,
          name: item.label,
        }))}
        action={
          headerButtons ? (
            <Box display="flex" gap="16px" {...(headerButtonProps ?? {})}>
              {headerButtons
                ? typeof headerButtons === 'function'
                  ? headerButtons({
                      defaultButtons: null,
                    })
                  : headerButtons
                : null}
            </Box>
          ) : undefined
        }
        sx={{mb: {xs: 3, md: 5}}}
      />
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: (theme) => theme.zIndex.drawer + 1,
            // this is needed to support custom themes, dark mode etc.
            bgcolor: (theme) => alpha(theme.palette.background.paper, 0.4),
          }}
        >
          <CircularProgress />
        </Box>
      )}
      {children}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '16px',
          padding: '16px',
        }}
        {...(footerButtonProps ?? {})}
      >
        {footerButtons
          ? typeof footerButtons === 'function'
            ? footerButtons({
                defaultButtons: defaultFooterButtons,
                saveButtonProps,
              })
            : footerButtons
          : defaultFooterButtons}
      </Box>
    </>
  );
};
